/**
 * Responsibility: make every rounded control exactly one height.
 *
 * The kit already sizes controls centrally and warns that per-component
 * sizing is how six different sizes end up on one page. It is right, and
 * these overrides exist because two of its rules leave a visible gap rather
 * than because the approach is wrong - which is why they live here, merged
 * through `AppTheme`'s `themeComponents` seam, instead of being edited into
 * `src/kit`. The kit has to stay a faithful copy that can be re-synced.
 *
 * `minHeight` is a floor, not a height. A small `ToggleButton` inherits MUI's
 * own vertical padding, which pushes it past the kit's 30px floor to 36 - so
 * a row of population filters stood 6px taller than the Refresh button beside
 * it. Setting the padding as well as the floor is what actually pins it.
 *
 * **These are composed onto the kit's rules, not substituted for them.**
 * `AppTheme` spreads `themeComponents` over its own customizations one level
 * deep, so exporting a bare `MuiButton` here would drop the kit's border
 * radius, font weight and icon sizing along with everything else under that
 * key. Measured: it silently took the button font from 0.72rem to the MUI
 * default. So each entry below starts from the kit's own and adds to it.
 *
 * Emphasis comes from `variant`, never from `size`. A contained button reads
 * as the primary action without being a different height from the one next
 * to it, and that is the rule the whole app now follows.
 */
import { inputsCustomizations } from '@kit/theme/customizations';
import type { ThemeOptions } from '@mui/material/styles';

/** One height for every control. Matches the kit's dense `heightSm`. */
export const CONTROL_HEIGHT = 30;

/** WCAG 2.5.8, applied on coarse pointers exactly as the kit does. */
export const TOUCH_HEIGHT = 44;

const coarse = (styles: Record<string, unknown>) => ({
  '@media (pointer: coarse)': styles,
});

/** Pins the box rather than only flooring it. */
const snug = {
  paddingTop: 0,
  paddingBottom: 0,
  minHeight: CONTROL_HEIGHT,
  height: CONTROL_HEIGHT,
  ...coarse({ minHeight: TOUCH_HEIGHT, height: TOUCH_HEIGHT }),
};

const kitButton = inputsCustomizations?.MuiButton ?? {};
const kitToggle = inputsCustomizations?.MuiToggleButton ?? {};
const kitToggleGroup = inputsCustomizations?.MuiToggleButtonGroup ?? {};

export const controlCustomizations: ThemeOptions['components'] = {
  MuiButton: {
    ...kitButton,
    // So a button that forgot its size prop is still the right size.
    defaultProps: { ...kitButton.defaultProps, size: 'small' },
    styleOverrides: {
      ...kitButton.styleOverrides,
      sizeSmall: {
        ...(kitButton.styleOverrides?.sizeSmall as object),
        ...snug,
      },
    },
  },

  MuiToggleButton: {
    ...kitToggle,
    styleOverrides: {
      ...kitToggle.styleOverrides,
      sizeSmall: {
        ...(kitToggle.styleOverrides?.sizeSmall as object),
        ...snug,
        paddingInline: 12,
        // The whole coarse-pointer block is replaced, not merged, because
        // it is one key. So the kit's width rule has to be restated: a
        // two-character option ("All", "5+") is otherwise 40px wide and
        // under the tap minimum however tall it is.
        ...coarse({
          minHeight: TOUCH_HEIGHT,
          height: TOUCH_HEIGHT,
          minWidth: TOUCH_HEIGHT,
          paddingInline: 14,
        }),
      },
    },
  },

  MuiToggleButtonGroup: {
    ...kitToggleGroup,
    styleOverrides: {
      ...kitToggleGroup.styleOverrides,
      root: [
        kitToggleGroup.styleOverrides?.root,
        { height: CONTROL_HEIGHT, ...coarse({ height: TOUCH_HEIGHT }) },
      ],
    },
  },
};
